import { Component, inject } from '@angular/core';
import { NgClass } from '@angular/common';

import { ToastLevel, ToastService } from './toast.service';

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [NgClass],
  styles: `
    .toast-viewport {
      position: fixed;
      /* ~20-24px from the top of the content area. */
      top: 1.375rem;
      /* Offset by the sidebar width so the stack centers within the main
         content, not the whole viewport (see --app-content-inset-left). */
      left: var(--app-content-inset-left, 0px);
      right: 0;
      z-index: 1000;
      display: flex;
      justify-content: center;
      padding: 0 1rem;
      /* The container never blocks the page; only the cards are interactive. */
      pointer-events: none;
    }

    @media (max-width: 767px) {
      .toast-viewport {
        top: 0.875rem;
      }
    }

    .toast-stack {
      display: flex;
      flex-direction: column;
      gap: 0.625rem;
      width: min(500px, 100%);
    }

    .toast-card {
      animation: toast-in 220ms cubic-bezier(0.16, 1, 0.3, 1);
      will-change: transform, opacity;
    }

    /* Toast cards keep their LIGHT pastel look in BOTH themes, so the copy must
       stay dark. Colors are pinned here (instead of text-slate-* / bg-*-50
       utilities) because the global dark-mode overrides in theme.scss remap
       those utilities (.text-slate-800 -> white !important, .bg-rose-50 ->
       dark) which made toast text unreadable in dark mode. */
    .toast-card__title {
      color: #1e293b;
    }

    .toast-card__message {
      color: #64748b;
    }

    .toast-card__close {
      color: #94a3b8;
    }

    .toast-card__close:hover {
      background-color: #f1f5f9;
      color: #475569;
    }

    .toast-card__close:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px #cbd5e1;
    }

    .toast-card--success {
      background-color: #ecfdf5;
      border-color: #a7f3d0;
      border-left: 4px solid #10b981;
    }

    .toast-card--error {
      background-color: #fff1f2;
      border-color: #fecdd3;
      border-left: 4px solid #f43f5e;
    }

    .toast-card--warning {
      background-color: #fffbeb;
      border-color: #fde68a;
      border-left: 4px solid #f59e0b;
    }

    .toast-card--info {
      background-color: #f0f9ff;
      border-color: #bae6fd;
      border-left: 4px solid #0ea5e9;
    }

    .toast-card--leaving {
      animation: toast-out 180ms ease forwards;
      pointer-events: none;
    }

    .toast-card__progress {
      animation: toast-progress var(--toast-duration, 4500ms) linear forwards;
    }

    /* Pause both the timer (JS) and the progress bar (CSS) on hover. */
    .toast-card:hover .toast-card__progress {
      animation-play-state: paused;
    }

    @keyframes toast-in {
      from {
        opacity: 0;
        transform: translateY(-14px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes toast-out {
      from {
        opacity: 1;
        transform: translateY(0);
      }
      to {
        opacity: 0;
        transform: translateY(-12px);
      }
    }

    @keyframes toast-progress {
      from {
        transform: scaleX(1);
      }
      to {
        transform: scaleX(0);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .toast-card,
      .toast-card--leaving,
      .toast-card__progress {
        animation: none;
      }
    }
  `,
  template: `
    <div class="toast-viewport" aria-live="polite" aria-atomic="true">
      <div class="toast-stack">
        @for (toast of messages(); track toast.id) {
          <div
            class="toast-card pointer-events-auto relative flex min-h-[72px] items-start gap-3 overflow-hidden rounded-xl border px-4 py-3.5 shadow-xl"
            [ngClass]="cardTypeClasses(toast.level)"
            [class.toast-card--leaving]="toast.leaving"
            [attr.role]="toast.role"
            [attr.aria-live]="toast.ariaLive"
            (mouseenter)="onPointerEnter(toast.id)"
            (mouseleave)="onPointerLeave(toast.id)"
          >
            <span class="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center" [ngClass]="iconColorClass(toast.level)">
              <i class="pi text-lg" [ngClass]="iconClass(toast.level)" aria-hidden="true"></i>
            </span>

            <div class="min-w-0 flex-1 pr-6">
              <p class="toast-card__title text-sm font-semibold leading-5">{{ toast.title }}</p>
              <p class="toast-card__message mt-0.5 line-clamp-3 text-[13px] leading-5">{{ toast.message }}</p>
            </div>

            <button
              type="button"
              class="toast-card__close absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-md transition"
              [attr.aria-label]="closeLabel"
              (click)="dismiss(toast.id)"
            >
              <i class="pi pi-times text-sm" aria-hidden="true"></i>
            </button>

            <span
              class="toast-card__progress absolute inset-x-0 bottom-0 h-1 origin-left"
              [ngClass]="progressColorClass(toast.level)"
              [style.--toast-duration]="toast.durationMs + 'ms'"
              aria-hidden="true"
            ></span>
          </div>
        }
      </div>
    </div>
  `
})
export class ToastComponent {
  private readonly toastService = inject(ToastService);

  protected readonly messages = this.toastService.messages;
  protected readonly closeLabel = 'Cerrar notificación';

  protected dismiss(toastId: string): void {
    this.toastService.dismiss(toastId);
  }

  protected onPointerEnter(toastId: string): void {
    this.toastService.pause(toastId);
  }

  protected onPointerLeave(toastId: string): void {
    this.toastService.resume(toastId);
  }

  protected cardTypeClasses(level: ToastLevel): string {
    switch (level) {
      case 'success':
        return 'toast-card--success';
      case 'error':
        return 'toast-card--error';
      case 'warning':
        return 'toast-card--warning';
      case 'info':
      default:
        return 'toast-card--info';
    }
  }

  protected iconClass(level: ToastLevel): string {
    switch (level) {
      case 'success':
        return 'pi-check-circle';
      case 'error':
        return 'pi-times-circle';
      case 'warning':
        return 'pi-exclamation-triangle';
      case 'info':
      default:
        return 'pi-info-circle';
    }
  }

  protected iconColorClass(level: ToastLevel): string {
    switch (level) {
      case 'success':
        return 'text-emerald-600';
      case 'error':
        return 'text-rose-600';
      case 'warning':
        return 'text-amber-600';
      case 'info':
      default:
        return 'text-sky-600';
    }
  }

  protected progressColorClass(level: ToastLevel): string {
    switch (level) {
      case 'success':
        return 'bg-emerald-500';
      case 'error':
        return 'bg-rose-500';
      case 'warning':
        return 'bg-amber-500';
      case 'info':
      default:
        return 'bg-sky-500';
    }
  }
}
